import type { MemoExoticComponent, ReactElement } from 'react'
import { memo, useCallback, useMemo } from 'react'

import { format as formatUtil } from 'date-fns'
import { useController, useFormContext } from 'react-hook-form'

import CalendarIcon from '@/assets/icons/icon-calendar.svg'
import { Button } from '@/components/ui/button.tsx'
import { Calendar } from '@/components/ui/calendar/calendar.tsx'
import { ErrorText } from '@/components/ui/error-text'
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from '@/components/ui/popover.tsx'
import { classNames } from '@/utils/helpers'

import {
  addMinute,
  constructDate,
  getFormattedTime,
  getIsPM,
  getIsSameDay,
} from '../../utils'
import { CalendarTimePicker } from './calendar-time-picker'

interface CalendarFormInputProps<T> {
  label?: string
  name: string
  fromDate?: Date
  toDate?: Date
  minTime?: string
  disabled?: boolean
  convertToDate?: (value: T) => Date
  convertFromDate?: (date: Date) => T
  defaultDate?: Date
  className?: string
  block?: boolean
  format?: string
  showError?: boolean
}

interface CalendarFormInputComponent {
  <T>(props: CalendarFormInputProps<T>): ReactElement | null
}

export const CalendarFormInput = memo(function CalendarFormInput<T>(
  props: CalendarFormInputProps<T>,
) {
  const {
    label,
    name,
    fromDate,
    toDate,
    disabled,
    convertFromDate,
    convertToDate,
    defaultDate,
    className,
    block,
    format = 'LLL dd, y hh:mm a',
    showError,
  } = props
  const { control } = useFormContext()
  const {
    field,
    fieldState: { error },
  } = useController({ name, control })

  const value = useMemo(
    () => {
      let date = defaultDate ? defaultDate : new Date()

      if (field.value) {
        date = convertToDate
          ? convertToDate(field.value)
          : new Date(field.value)
      }

      return date
    }, // eslint-disable-next-line react-hooks/exhaustive-deps
    [convertToDate, field.value],
  )

  const changeSelectedDate = useCallback(
    (day: Date | undefined) => {
      if (!day) {
        return
      }

      const fieldValue = convertToDate
        ? convertToDate(field.value)
        : new Date(field.value)
      const isPM = getIsPM(fieldValue)
      const date = constructDate(day, getFormattedTime(fieldValue), isPM)
      const value = convertFromDate ? convertFromDate(date) : date

      field.onChange(value)
    },
    [convertFromDate, convertToDate, field],
  )

  const getMinTimeForEndDate = useCallback(
    (startDate: Date | undefined, endDate: Date) => {
      if (!startDate) {
        return
      }

      const isSameDay = getIsSameDay(startDate, endDate)

      if (isSameDay) {
        const minTime = addMinute(startDate)

        return getFormattedTime(minTime)
      }
    },
    [],
  )

  return (
    <>
      <div className={classNames('grid gap-2', { 'w-full': block })}>
        {label && <h3 className="text-sm font-medium text-title">{label}</h3>}
        <Popover>
          <PopoverTrigger asChild>
            <Button
              id="date"
              variant="ghost"
              disabled={disabled}
              className={classNames(
                'flex w-[240px] items-center justify-start border bg-background text-left font-normal text-title',
                className,
                {
                  'cursor-not-allowed': disabled,
                  'w-full': block,
                },
              )}
            >
              <CalendarIcon className="mr-2 h-4 w-4 flex-shrink-0" />
              {value && formatUtil(value, format)}
            </Button>
          </PopoverTrigger>
          <PopoverContent
            className="z-100 w-auto p-3.5"
            align="end"
          >
            <Calendar
              initialFocus
              mode="single"
              defaultMonth={value}
              selected={value}
              onSelect={changeSelectedDate}
              numberOfMonths={1}
              fromDate={fromDate}
              toDate={toDate}
              footer={
                <div className="mt-6 flex items-center justify-between">
                  <span className="text-label-primary text-base">Time</span>
                  <CalendarTimePicker
                    name={name}
                    fromDate={fromDate}
                    defaultDate={defaultDate}
                    minTime={getMinTimeForEndDate(fromDate, field.value)}
                    convertToDate={convertToDate}
                    convertFromDate={convertFromDate}
                  />
                </div>
              }
            />
          </PopoverContent>
        </Popover>
      </div>
      {showError && error && <ErrorText>{error?.message}</ErrorText>}
    </>
  )
}) satisfies MemoExoticComponent<CalendarFormInputComponent> as CalendarFormInputComponent
// Cast here is required to support generics
